/**
 * Vulnerability-disclosure policy content (CRA, work item opsanio/documentation#333).
 *
 * Deliberately React-free and free of the i18n runtime, because two very
 * different consumers read it:
 *
 *   - routes/security.ts (server) builds /.well-known/security.txt from it
 *   - src/homepage/security.tsx renders it as the /security page
 *
 * Keeping both on one module is the point: an RFC 9116 record whose Policy URL
 * promises something the page does not say is a compliance defect, and that is
 * exactly the drift that happens when the two are maintained separately.
 *
 * The policy text is NOT in translations/{de,en}.json like the imprint's is.
 * The work item pins one language per URL (/security German, /security/en
 * English), so the copy has to be selectable by route rather than by the
 * visitor's app language - which is what the translation runtime keys off.
 */

/** Vendor facts. Verbatim from the autonubil company record - never reword. */
export const SECURITY_VENDOR = {
  legalName: "autonubil GmbH",
  street: "Wilma-Witte-Stieg 1",
  city: "22047 Hamburg",
  cityEn: "22047 Hamburg, Germany",
  directors: "Stephan Beckmann, Christian Nützmann",
  court: "Amtsgericht Hamburg",
  courtEn: "District Court Hamburg",
  hrb: "HRB 134661",
  vatId: "DE363688213",
} as const;

export const SECURITY_CONTACT = "security@autonubil.de";

export const SECURITY_POLICY_URL = {
  de: "https://autonubil.de/security",
  en: "https://autonubil.de/security/en",
} as const;

/**
 * RFC 9116 requires exactly one Expires, and readers MUST ignore the file once
 * it has passed. Renew this well before the date and bump SECURITY_LAST_UPDATED
 * in the same change; tests/security/security-txt.test.ts fails once the date is
 * inside 90 days, so it cannot lapse unnoticed.
 */
export const SECURITY_TXT_EXPIRES = "2027-09-04T23:59:59Z";

export const SECURITY_LAST_UPDATED = {
  de: "4. September 2026",
  en: "4 September 2026",
} as const;

/** Every host that serves the file. RFC 9116: each URI must resolve to it. */
export const SECURITY_CANONICAL_URLS = [
  "https://autonubil.de/.well-known/security.txt",
  "https://anketa.exanio.cloud/.well-known/security.txt",
];

export const securityTxt = [
  `# Security contact for ${SECURITY_VENDOR.legalName}`,
  "",
  `Contact: mailto:${SECURITY_CONTACT}`,
  `Expires: ${SECURITY_TXT_EXPIRES}`,
  `Policy: ${SECURITY_POLICY_URL.de}`,
  `Preferred-Languages: de, en`,
  ...SECURITY_CANONICAL_URLS.map((url) => `Canonical: ${url}`),
  "",
].join("\n");

export type SecurityLang = "de" | "en";

/** Icon slot per section. The page maps these to lucide components; keeping
 *  them as names is what lets this module stay importable from the server. */
export type SecurityIcon =
  | "report"
  | "researchers"
  | "scope"
  | "commitment"
  | "disclosure"
  | "safeHarbor"
  | "changes";

export type SecurityBlock = { p: string } | { ul: string[] };

export interface SecuritySection {
  icon: SecurityIcon;
  heading: string;
  blocks: SecurityBlock[];
}

export interface SecurityPolicy {
  lang: SecurityLang;
  path: string;
  eyebrow: string;
  title: string;
  intro: string;
  lastUpdatedLabel: string;
  contactLabel: string;
  contactHint: string;
  sections: SecuritySection[];
  /** Label for the link to the other language, in that other language. */
  altLangLabel: string;
  altLangPath: string;
  securityTxtLabel: string;
  legalBlock: string[];
}

const DE: SecurityPolicy = {
  lang: "de",
  path: "/security",
  eyebrow: "Sicherheit",
  title: "Richtlinie zur Meldung von Sicherheitslücken",
  intro: `${SECURITY_VENDOR.legalName} arbeitet daran, die eigene Software und die eigenen Dienste sicher zu halten. Wir schätzen die Arbeit von Sicherheitsforschenden und Kundinnen und Kunden, die uns dabei helfen, Sicherheitslücken zu finden und verantwortungsvoll zu melden.`,
  lastUpdatedLabel: `Stand: ${SECURITY_LAST_UPDATED.de}`,
  contactLabel: "Meldungen an",
  contactHint:
    "Wir bestätigen den Eingang Ihrer Meldung innerhalb von fünf Werktagen.",
  sections: [
    {
      icon: "report",
      heading: "Eine Sicherheitslücke melden",
      blocks: [
        {
          p: `Wenn Sie glauben, in einem unserer Produkte oder Dienste eine Sicherheitslücke gefunden zu haben, melden Sie diese bitte an ${SECURITY_CONTACT}.`,
        },
        { p: "Bitte geben Sie dabei so viele Informationen wie möglich an:" },
        {
          ul: [
            "eine Beschreibung der Sicherheitslücke und ihrer möglichen Auswirkungen",
            "das betroffene Produkt, den betroffenen Dienst, die Version oder die URL",
            "die Schritte zur Reproduktion",
            "sofern sinnvoll: Proof-of-Concept-Code oder Screenshots",
            "alle weiteren Informationen, die uns bei der Untersuchung helfen",
          ],
        },
        {
          p: "Bitte übermitteln Sie keine Passwörter, personenbezogenen Daten oder sonstigen vertraulichen Informationen, es sei denn, sie sind zum Nachweis der Sicherheitslücke erforderlich.",
        },
      ],
    },
    {
      icon: "researchers",
      heading: "Was wir von Sicherheitsforschenden erbitten",
      blocks: [
        {
          p: "Damit wir Sicherheitslücken gefahrlos untersuchen können, bitten wir Sie:",
        },
        {
          ul: [
            "uns angemessen Zeit zur Untersuchung und Behebung zu geben, bevor Sie die Lücke öffentlich machen",
            "nicht auf Daten anderer Nutzerinnen und Nutzer zuzugreifen und diese nicht zu verändern, zu löschen oder herunterzuladen",
            "Handlungen zu unterlassen, die unsere Dienste unterbrechen oder in ihrer Verfügbarkeit beeinträchtigen könnten",
            "auf Social Engineering, Phishing, physische Angriffe, Denial-of-Service-Angriffe und automatisiertes Scannen mit hohem Volumen gegen unsere Systeme zu verzichten",
            "die Tests abzubrechen und uns zu kontaktieren, wenn Sie auf sensible oder personenbezogene Daten stoßen, die Ihnen nicht gehören",
          ],
        },
      ],
    },
    {
      icon: "scope",
      heading: "Geltungsbereich",
      blocks: [
        {
          p: "Diese Richtlinie gilt für Sicherheitslücken, die Folgendes betreffen:",
        },
        {
          ul: [
            "unsere öffentlich erreichbaren Webanwendungen und APIs",
            "unsere offiziellen Softwareprodukte und Dienste",
            `Infrastruktur, die unmittelbar von der ${SECURITY_VENDOR.legalName} betrieben wird`,
          ],
        },
        {
          p: "Dienste, Produkte und Websites Dritter fallen nicht unter diese Richtlinie.",
        },
        {
          p: "Wenn Sie nicht sicher sind, ob ein System zum Geltungsbereich gehört, kontaktieren Sie uns bitte, bevor Sie Sicherheitstests durchführen.",
        },
      ],
    },
    {
      icon: "commitment",
      heading: "Unsere Zusage",
      blocks: [
        { p: "Wir bemühen uns nach Kräften darum," },
        {
          ul: [
            "den Eingang Ihrer Meldung innerhalb von fünf Werktagen zu bestätigen",
            "die gemeldete Sicherheitslücke zu untersuchen",
            "Sie, soweit angemessen, über den Stand der Untersuchung zu informieren",
            "bestätigte Sicherheitslücken entsprechend ihrem Schweregrad und ihren möglichen Auswirkungen zu beheben",
            "Sicherheitsforschende zu nennen, die genannt werden möchten, sofern sie nicht anonym bleiben wollen",
          ],
        },
        {
          p: "Im Laufe der Untersuchung fragen wir gegebenenfalls weitere Informationen bei Ihnen nach.",
        },
      ],
    },
    {
      icon: "disclosure",
      heading: "Verantwortungsvolle Offenlegung",
      blocks: [
        {
          p: "Wir bitten Sicherheitsforschende, eine Sicherheitslücke nicht öffentlich zu machen, bevor wir angemessen Gelegenheit hatten, sie zu untersuchen und zu beheben.",
        },
        {
          p: "Über einen angemessenen Zeitplan für die Offenlegung sprechen wir gerne mit Ihnen. Unser Ziel ist die gemeinsame Arbeit am Schutz unserer Kundinnen und Kunden sowie unserer Nutzerinnen und Nutzer.",
        },
      ],
    },
    {
      icon: "safeHarbor",
      heading: "Rechtlicher Schutz (Safe Harbor)",
      blocks: [
        {
          p: "Wird Sicherheitsforschung im Einklang mit dieser Richtlinie betrieben, betrachten wir sie als",
        },
        {
          ul: [
            "autorisiert und nach Treu und Glauben durchgeführt,",
            "nicht auf Schädigung gerichtet und",
            "keinen Anlass, allein wegen dieser Sicherheitsforschung rechtliche Schritte einzuleiten.",
          ],
        },
        {
          p: "Diese Richtlinie autorisiert keine Handlungen, die geltendes Recht oder Rechte Dritter verletzen.",
        },
        {
          p: `Wenn Sie nicht sicher sind, ob eine geplante Aktivität zulässig ist, wenden Sie sich bitte vorab an ${SECURITY_CONTACT}.`,
        },
      ],
    },
    {
      icon: "changes",
      heading: "Änderungen dieser Richtlinie",
      blocks: [
        {
          p: `Wir können diese Richtlinie von Zeit zu Zeit aktualisieren. Die jeweils aktuelle Fassung wird stets unter ${SECURITY_POLICY_URL.de} veröffentlicht.`,
        },
      ],
    },
  ],
  altLangLabel: "English",
  altLangPath: "/security/en",
  securityTxtLabel: "security.txt",
  legalBlock: [
    `${SECURITY_VENDOR.legalName} · ${SECURITY_VENDOR.street} · ${SECURITY_VENDOR.city}`,
    `Geschäftsführer: ${SECURITY_VENDOR.directors}`,
    `Registergericht: ${SECURITY_VENDOR.court} · ${SECURITY_VENDOR.hrb}`,
    `USt-IdNr.: ${SECURITY_VENDOR.vatId}`,
  ],
};

const EN: SecurityPolicy = {
  lang: "en",
  path: "/security/en",
  eyebrow: "Security",
  title: "Security Vulnerability Disclosure Policy",
  intro: `${SECURITY_VENDOR.legalName} is committed to keeping our software and services secure. We appreciate the work of security researchers and customers who help us identify and responsibly report security vulnerabilities.`,
  lastUpdatedLabel: `Last updated: ${SECURITY_LAST_UPDATED.en}`,
  contactLabel: "Report to",
  contactHint: "We acknowledge reports within 5 business days.",
  sections: [
    {
      icon: "report",
      heading: "Reporting a vulnerability",
      blocks: [
        {
          p: `If you believe you have found a security vulnerability in one of our products or services, please report it to ${SECURITY_CONTACT}.`,
        },
        { p: "Please provide as much information as possible, including:" },
        {
          ul: [
            "a description of the vulnerability and its potential impact",
            "the affected product, service, version, or URL",
            "steps to reproduce the issue",
            "proof-of-concept code or screenshots, where appropriate",
            "any other information that may help us investigate the issue",
          ],
        },
        {
          p: "Please do not include passwords, personal data, or other confidential information unless it is necessary to demonstrate the vulnerability.",
        },
      ],
    },
    {
      icon: "researchers",
      heading: "What we ask of security researchers",
      blocks: [
        { p: "To help us investigate vulnerabilities safely, we ask that you:" },
        {
          ul: [
            "give us reasonable time to investigate and address the issue before publicly disclosing it",
            "avoid accessing, modifying, deleting, or downloading data belonging to other users",
            "avoid actions that could interrupt or degrade the availability of our services",
            "do not use social engineering, phishing, physical attacks, denial-of-service attacks, or automated high-volume scanning against our systems",
            "stop testing and contact us if you encounter sensitive or personal information that does not belong to you",
          ],
        },
      ],
    },
    {
      icon: "scope",
      heading: "Scope",
      blocks: [
        { p: "This policy applies to security vulnerabilities affecting:" },
        {
          ul: [
            "our publicly available web applications and APIs",
            "our official software products and services",
            `infrastructure directly operated by ${SECURITY_VENDOR.legalName}`,
          ],
        },
        {
          p: "Third-party services, products, and websites are outside the scope of this policy.",
        },
        {
          p: "If you are unsure whether a system is in scope, please contact us before performing security testing.",
        },
      ],
    },
    {
      icon: "commitment",
      heading: "Our commitment",
      blocks: [
        { p: "We will make reasonable efforts to:" },
        {
          ul: [
            "acknowledge your report within 5 business days",
            "investigate the reported vulnerability",
            "keep you informed about the status of the investigation where appropriate",
            "address confirmed vulnerabilities according to their severity and potential impact",
            "credit researchers who wish to be acknowledged, unless they prefer to remain anonymous",
          ],
        },
        { p: "We may ask for additional information during our investigation." },
      ],
    },
    {
      icon: "disclosure",
      heading: "Responsible disclosure",
      blocks: [
        {
          p: "We ask researchers not to publicly disclose a vulnerability until we have had a reasonable opportunity to investigate and remediate it.",
        },
        {
          p: "We are happy to discuss an appropriate disclosure timeline with the researcher. Our goal is to work collaboratively toward protecting our customers and users.",
        },
      ],
    },
    {
      icon: "safeHarbor",
      heading: "Legal safe harbor",
      blocks: [
        {
          p: "When security research is conducted in accordance with this policy, we will consider the research to be:",
        },
        {
          ul: [
            "authorized and conducted in good faith;",
            "not intended to cause harm; and",
            "not a basis for initiating legal action solely because of the security research.",
          ],
        },
        {
          p: "This policy does not authorize actions that violate applicable law or the rights of third parties.",
        },
        {
          p: `If you are unsure whether a planned activity is permitted, please contact us at ${SECURITY_CONTACT} before proceeding.`,
        },
      ],
    },
    {
      icon: "changes",
      heading: "Changes to this policy",
      blocks: [
        {
          p: `We may update this policy from time to time. The current version will always be published at ${SECURITY_POLICY_URL.en}.`,
        },
      ],
    },
  ],
  altLangLabel: "Deutsch",
  altLangPath: "/security",
  securityTxtLabel: "security.txt",
  legalBlock: [
    `${SECURITY_VENDOR.legalName} · ${SECURITY_VENDOR.street} · ${SECURITY_VENDOR.cityEn}`,
    `Managing Directors: ${SECURITY_VENDOR.directors}`,
    `Register Court: ${SECURITY_VENDOR.courtEn} · ${SECURITY_VENDOR.hrb}`,
    `VAT ID: ${SECURITY_VENDOR.vatId}`,
  ],
};

export const SECURITY_POLICY: Record<SecurityLang, SecurityPolicy> = {
  de: DE,
  en: EN,
};
